import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { Modal, ModalFooter } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input, Textarea, Label, Select } from '../ui/Input';
import { useToast } from '../../context/ToastContext';

const userCollections = [
  { label: 'Alumni', name: 'alumni' },
  { label: 'Moderators', name: 'moderator' },
  { label: 'Coaches', name: 'coaches' },
  { label: 'Clients', name: 'clients' },
];

const priorities = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const statuses = [
  { value: 'pending', label: 'Pending' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

const TaskModal = ({ open, onClose, onSave, task }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    dueDate: '',
    estimatedHours: '',
    notes: '',
    priority: 'medium',
    status: 'pending',
    category: '',
    assignedTo: '',
    clientId: '',
    clientName: '',
  });
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const toast = useToast();

  useEffect(() => {
    if (task) {
      const taskData = { ...task };
      // Handle Firestore Timestamp conversion for dueDate
      if (taskData.dueDate) {
        const dueDate = taskData.dueDate?.toDate?.() || taskData.dueDate;
        if (dueDate instanceof Date) {
          taskData.dueDate = dueDate.toISOString().split('T')[0];
        }
      }
      setForm({ ...form, ...taskData });
    } else {
      setForm({
        title: '',
        description: '',
        dueDate: '',
        estimatedHours: '',
        notes: '',
        priority: 'medium',
        status: 'pending',
        category: '',
        assignedTo: '',
        clientId: '',
        clientName: '',
      });
    }
  }, [task, open]);

  useEffect(() => {
    if (!open) return;

    setLoadingUsers(true);
    Promise.all(
      userCollections.map(async ({ name, label }) => {
        try {
          const snap = await getDocs(collection(firestore, name));
          return snap.docs.map(doc => ({
            email: doc.data().email,
            name:
              doc.data().firstname && doc.data().lastname
                ? `${doc.data().firstname} ${doc.data().lastname}`
                : doc.data().name || doc.data().email,
            id: doc.id,
            type: label,
          }));
        } catch (error) {
          console.error(`Error loading ${name}:`, error);
          return [];
        }
      })
    )
      .then(results => {
        setUsers(results.flat());
        setLoadingUsers(false);
      })
      .catch(error => {
        toast.error('Failed to load users', 'Error');
        setLoadingUsers(false);
      });
  }, [open, toast]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleUserSelect = e => {
    const email = e.target.value;
    const user = users.find(u => u.email === email);
    setForm(f => ({
      ...f,
      assignedTo: email,
      clientId: user?.id || '',
      clientName: user?.name || '',
    }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!form.title || !form.description || !form.dueDate || !form.assignedTo) {
      toast.warning('Please fill in all required fields', 'Validation Error');
      return;
    }
    onSave(form);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={task ? 'Edit Task' : 'Create Task'}
      description={task ? 'Update task details below' : 'Fill in the details to create a new task'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="title" required>
              Title
            </Label>
            <Input
              id="title"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Enter task title"
              required
            />
          </div>

          <div>
            <Label htmlFor="description" required>
              Description
            </Label>
            <Textarea
              id="description"
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Enter task description"
              rows={4}
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="dueDate" required>
                Due Date
              </Label>
              <Input
                id="dueDate"
                name="dueDate"
                type="date"
                value={form.dueDate}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="estimatedHours">Estimated Hours</Label>
              <Input
                id="estimatedHours"
                name="estimatedHours"
                type="number"
                value={form.estimatedHours}
                onChange={handleChange}
                min={0}
                placeholder="0"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Input
              id="notes"
              name="notes"
              value={form.notes}
              onChange={handleChange}
              placeholder="Additional notes (optional)"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="priority">Priority</Label>
              <Select id="priority" name="priority" value={form.priority} onChange={handleChange}>
                {priorities.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select id="status" name="status" value={form.status} onChange={handleChange}>
                {statuses.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              name="category"
              value={form.category}
              onChange={handleChange}
              placeholder="Task category (optional)"
            />
          </div>

          <div>
            <Label htmlFor="assignedTo" required>
              Assign To
            </Label>
            {loadingUsers ? (
              <div className="flex h-10 items-center rounded-lg border border-border bg-surface px-3 text-sm text-text-tertiary">
                Loading users...
              </div>
            ) : (
              <Select
                id="assignedTo"
                name="assignedTo"
                value={form.assignedTo}
                onChange={handleUserSelect}
                required
              >
                <option value="">Select user...</option>
                {users.map(user => (
                  <option key={user.email} value={user.email}>
                    {user.name} ({user.email}) - {user.type}
                  </option>
                ))}
              </Select>
            )}
          </div>
        </div>

        <ModalFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">{task ? 'Update Task' : 'Create Task'}</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default TaskModal;
